import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useCallback } from 'react';

import FeaturedImage from 'components/Landings/FeaturedImage';
import Layout from 'components/Layout';
import OffersList from 'components/Timeline/OffersList';
import OfferPageOffersSortBox from 'components/Timeline/OffersSortBox';
import WishlistSuggestion from 'components/Timeline/WishlistSuggestion';
import BlogArticles from 'components/pages/OfferPages/BlogArticles';

import useOffersSort from 'hooks/useOffersSort';

import { canonize } from 'lib/links';

import OfferPagesHeading from './Heading';
import OfferPagesSchema from './Schema';
import OffersPageSidebar from './Sidebar';

const GenericOfferPage = ({
  apiEndpointRequestFn,
  className,
  description = null,
  featuredImage = null,
  gaEventCategory,
  headingTitle,
  hideBreadcrumbOnMobile = false,
  offersLimit = 24,
  offersListProps = {},
  offersSortValue = null,
  pageDescriptionComponent = null,
  pageTitle,
  queryKey,
  relatedArticles = [],
  serverOffers,
  sidebarProps = {},
  withoutSortBox = false,
  withWishlistSuggestion = false,
}) => {
  const { options, offersSort, setOffersSort } = useOffersSort();
  const { asPath } = useRouter();

  const onSelectChangeHandler = ({ target }) => setOffersSort(target.value);

  const queryFn = useCallback(
    ({ after }, opts) =>
      apiEndpointRequestFn(
        {
          after,
          limit: offersLimit,
          sort: withoutSortBox ? offersSortValue : offersSort,
        },
        opts
      ),
    [withoutSortBox, offersSortValue, offersSort]
  );

  return (
    <>
      <NextSeo
        description={description}
        openGraph={{
          title: pageTitle,
          description,
        }}
        title={pageTitle}
      />
      <OfferPagesSchema href={canonize(asPath)} title={headingTitle} />
      {featuredImage && <FeaturedImage {...featuredImage} />}
      <Layout
        className={className}
        gridProps={{ className: 'mb-8' }}
        headingComponent={
          <OfferPagesHeading
            description={description}
            hideBreadcrumbOnMobile={hideBreadcrumbOnMobile}
            title={headingTitle}
          />
        }
        sidebarComponent={<OffersPageSidebar {...sidebarProps} />}
      >
        {!withoutSortBox && (
          <OfferPageOffersSortBox
            options={options}
            onSelectChange={onSelectChangeHandler}
          />
        )}
        <OffersList
          className={!withoutSortBox && 'mt-4'}
          gaEventCategory={gaEventCategory}
          initialOffers={serverOffers}
          queryFn={queryFn}
          queryKey={
            typeof queryKey === 'function' ? queryKey(offersSort) : queryKey
          }
          paginationCappedAtFinishedOffer
          {...offersListProps}
        />
        {relatedArticles.length > 0 && (
          <BlogArticles articles={relatedArticles} />
        )}
        {withWishlistSuggestion && (
          <WishlistSuggestion gaEventCategory={gaEventCategory} />
        )}
        {pageDescriptionComponent}
      </Layout>
    </>
  );
};

GenericOfferPage.propTypes = {
  apiEndpointRequestFn: PropTypes.func.isRequired,
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  description: PropTypes.string,
  gaEventCategory: PropTypes.string.isRequired,
  featuredImage: PropTypes.shape({
    bgcolor: PropTypes.arrayOf(PropTypes.string).isRequired,
    description: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
    mobileImage: PropTypes.string,
  }),
  headingTitle: PropTypes.string.isRequired,
  hideBreadcrumbOnMobile: PropTypes.bool,
  offersListProps: PropTypes.shape(),
  offersLimit: PropTypes.number,
  offersSortValue: PropTypes.string,
  queryKey: PropTypes.oneOfType([PropTypes.array, PropTypes.func]).isRequired,
  serverOffers: PropTypes.shape({
    after: PropTypes.string,
    sort: PropTypes.string.isRequired,
    before: PropTypes.number,
    offers: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        offerComments: PropTypes.number.isRequired,
        offerId: PropTypes.number.isRequired,
        offerIsHighlight: PropTypes.bool.isRequired,
        ratings: PropTypes.shape(),
        offerOldPrice: PropTypes.number,
        offerPhoto: PropTypes.string.isRequired,
        offerPrice: PropTypes.number.isRequired,
        offerPriceType: PropTypes.string.isRequired,
        offerPublished: PropTypes.string.isRequired,
        offerSlug: PropTypes.string.isRequired,
        offerStatusName: PropTypes.string.isRequired,
        offerUserVisibility: PropTypes.string.isRequired,
        offerTags: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string.isRequired,
            type: PropTypes.string.isRequired,
          })
        ),
        offerTitle: PropTypes.string.isRequired,
        storeDomain: PropTypes.string.isRequired,
      }).isRequired
    ).isRequired,
  }).isRequired,
  sidebarProps: PropTypes.shape(),
  pageDescriptionComponent: PropTypes.any,
  pageTitle: PropTypes.string.isRequired,
  relatedArticles: PropTypes.arrayOf(
    PropTypes.shape({
      articleDescription: PropTypes.string.isRequired,
      articleId: PropTypes.number.isRequired,
      articleImage: PropTypes.string.isRequired,
      articlePublished: PropTypes.string.isRequired,
      articleSlug: PropTypes.string.isRequired,
      articleThumbnail: PropTypes.string.isRequired,
      articleTitle: PropTypes.string.isRequired,
      commentCount: PropTypes.number.isRequired,
      userName: PropTypes.string.isRequired,
      userPhoto: PropTypes.string.isRequired,
      userUsername: PropTypes.string.isRequired,
    })
  ),
  withWishlistSuggestion: PropTypes.bool,
  withoutSortBox: PropTypes.bool,
};

export default GenericOfferPage;
